import React from 'react';
import { useIntl } from 'react-intl';
import RadialChart from './RadialChart';
import MatrixCard from './MatrixCard';
import BellCurveChart from './BellCurveChart';
import ProgressCard from './ProgressCard';
import ValueAccelerationSprintContainer from '../../../containers/ValueAccelerationSprintContainer';
import { shouldShowAssessmentPrompt } from '../../../utils/unifiedIntake';
import AssessmentPromptEmptyState from '../../Succession/AssessmentPromptEmptyState';

// Presentational-only tab. All data should be supplied via props by a container.
// Props shape:
// - vmi: { score: number 0..100, segments?: Array<{label, value}> }
// - readinessAttractiveness: { readiness: number, attractiveness: number }
// - profitDistribution: { low: number, avg: number, high: number, marker: number }
// - wealthProgress: { label: string, percent: number, amount?: number }
// - valueProgress: { label: string, percent: number, amount?: number }
// - sprint: { inProgress: Item[], done: Item[] } where Item = { id, title, priority?: 'low'|'med'|'high', assignee?: string }

const ValueAccelerationTab = ({
  className = '',
  vmi = { score: 62 },
  readinessAttractiveness = { readiness: 18, attractiveness: 22 },
  profitDistribution = { low: 5, avg: 8, high: 12, marker: 7.3 },
  profitProgress = { label: 'Profit Gap', percent: 0, amount: 0 },
  wealthProgress = { label: 'Wealth Gap', percent: 42, amount: 350000 },
  valueProgress = { label: 'Value Gap', percent: 28, amount: 1200000 },
  sprint = { inProgress: [], done: [] },
  currentUser,
  suppressPrompt = false,
}) => {
  const intl = useIntl();
  const showPrompt = !suppressPrompt && shouldShowAssessmentPrompt(currentUser);
  if (showPrompt) {
    return (
      <div className={`space-y-4 ${className}`}>
        <AssessmentPromptEmptyState placement="value_tab" />
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-succession-border p-3 lg:col-span-1" data-pol-id="ojlh1p">
          <RadialChart title={intl.formatMessage({ id: 'succession.value_acceleration.tab.value_maturity_index' })} data={vmi.data || []} />
        </div>
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-succession-border p-3" data-pol-id="nsbuyx">
            <MatrixCard readiness={readinessAttractiveness.readiness} attractiveness={readinessAttractiveness.attractiveness} />
          </div>
          <div className="bg-white rounded-lg border border-succession-border p-3" data-pol-id="wbpeig">
            <BellCurveChart low={profitDistribution.low} avg={profitDistribution.avg} high={profitDistribution.high} marker={profitDistribution.marker} />
          </div>
        </div>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-pol-id="wtlxx6">
        <div className="bg-white rounded-lg border border-succession-border p-3">
          <ProgressCard label={profitProgress.label} percent={profitProgress.percent} amount={profitProgress.amount} />
        </div>
        <div className="bg-white rounded-lg border border-succession-border p-3">
          <ProgressCard label={wealthProgress.label} percent={wealthProgress.percent} amount={wealthProgress.amount} />
        </div>
        <div className="bg-white rounded-lg border border-succession-border p-3">
          <ProgressCard label={valueProgress.label} percent={valueProgress.percent} amount={valueProgress.amount} />
        </div>
      </div>

      {/* Row 3 */}
      <div className="bg-white rounded-lg border border-succession-border p-3" data-pol-id="i6vme4">
        <ValueAccelerationSprintContainer />
      </div>
    </div>
  );
};

export default ValueAccelerationTab;


